import { Form, Row, Col, Button, ButtonGroup, ToggleButton } from 'react-bootstrap';
import type { SendMode, PushNotificationForm as FormData } from '../../../../apps/hooks/push-notification/usePushNotification';

interface PushNotificationFormProps {
  form: FormData;
  onChange: (field: keyof FormData, value: string) => void;
  onSendModeChange: (mode: SendMode) => void;
  onSubmit: () => void;
  isSending: boolean;
}

export const PushNotificationForm = ({ form, onChange, onSendModeChange, onSubmit, isSending }: PushNotificationFormProps) => {
  const isValid =
    form.title.trim() !== '' &&
    form.body.trim() !== '' &&
    form.url.trim() !== '' &&
    (form.sendMode === 'all' || form.receiverIdentifier.trim() !== '');

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        if (isValid) onSubmit();
      }}
    >
      {/* Send Mode Toggle */}
      <Form.Group className="mb-4">
        <Form.Label className="fw-semibold">Send Mode</Form.Label>
        <div>
          <ButtonGroup>
            <ToggleButton
              id="mode-all"
              type="radio"
              variant={form.sendMode === 'all' ? 'primary' : 'outline-primary'}
              value="all"
              checked={form.sendMode === 'all'}
              onChange={() => onSendModeChange('all')}
            >
              Send to All Users
            </ToggleButton>
            <ToggleButton
              id="mode-user"
              type="radio"
              variant={form.sendMode === 'user' ? 'primary' : 'outline-primary'}
              value="user"
              checked={form.sendMode === 'user'}
              onChange={() => onSendModeChange('user')}
            >
              Send to Specific User
            </ToggleButton>
          </ButtonGroup>
        </div>
      </Form.Group>

      {/* Receiver (only for specific user mode) */}
      {form.sendMode === 'user' && (
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Receiver Type</Form.Label>
              <Form.Select
                value={form.receiverType}
                onChange={(e) => onChange('receiverType', e.target.value)}
              >
                <option value="UserId">User ID</option>
                <option value="Session">Session Key</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label>Identifier</Form.Label>
              <Form.Control
                type="text"
                placeholder={form.receiverType === 'UserId' ? 'e.g. 42' : 'e.g. t_abc123...'}
                value={form.receiverIdentifier}
                onChange={(e) => onChange('receiverIdentifier', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {/* Title */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Title</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. New Year Sale: Up to 70% OFF!"
          value={form.title}
          onChange={(e) => onChange('title', e.target.value)}
          maxLength={100}
        />
        <Form.Text className="text-muted">{form.title.length}/100</Form.Text>
      </Form.Group>

      {/* Body */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Body</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="e.g. Discover what's trending in K-beauty this February..."
          value={form.body}
          onChange={(e) => onChange('body', e.target.value)}
          maxLength={300}
        />
        <Form.Text className="text-muted">{form.body.length}/300</Form.Text>
      </Form.Group>

      {/* Deep Link URL */}
      <Form.Group className="mb-4">
        <Form.Label className="fw-semibold">Deep Link URL</Form.Label>
        <Form.Control
          type="url"
          placeholder="e.g. https://skin-seoul.com/campaign/new-year-sale/?utm_source=app_push"
          value={form.url}
          onChange={(e) => onChange('url', e.target.value)}
        />
        <Form.Text className="text-muted">Full URL including UTM parameters.</Form.Text>
      </Form.Group>

      {/* Submit */}
      <Button variant={form.sendMode === 'all' ? 'danger' : 'primary'} type="submit" disabled={!isValid || isSending} size="lg">
        {isSending ? 'Sending...' : form.sendMode === 'all' ? 'Send to All Devices' : 'Send to User'}
      </Button>
    </Form>
  );
};
